<template>
  <div class="multiple-pets-container">
    <div class="pets-header">
      <h2 class="pets-title">Who is Your Oxytocin?</h2>
      <p class="pets-subtitle">Please input your pet's information</p>
    </div>

    <div class="pets-list">
      <!-- Existing pets -->
      <div v-for="(pet, index) in pets" :key="index" class="pet-item">
        <div class="pet-name-tag">{{ pet.name }}</div>
        <div class="pet-species-tag">{{ pet.species }}</div>
        <button class="remove-pet" @click="removePet(index)">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="#999" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <!-- Add new pet form -->
      <div class="add-pet-form">
        <div class="input-group">
          <div class="input-wrapper">
            <span class="input-icon">✏️</span>
            <input 
              type="text" 
              v-model="newPet.name" 
              placeholder="Name"
              class="pet-input"
              @keyup.enter="addPetIfValid"
            />
          </div>
          <div class="input-wrapper">
            <span class="input-icon">✏️</span>
            <input 
              type="text" 
              v-model="newPet.species" 
              placeholder="Species"
              class="pet-input"
              @keyup.enter="addPetIfValid"
            />
          </div>
        </div>
      </div>

      <!-- Add button -->
      <button class="add-pet-button" @click="addPet" :disabled="!canAddPet">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <!-- Navigation -->
    <div class="navigation-container">
      <button class="nav-arrow left" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <div class="pets-count">{{ pets.length }} pet{{ pets.length !== 1 ? 's' : '' }} added</div>
      
      <button class="nav-arrow right" @click="proceed" :disabled="pets.length === 0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiplePetsInput',
  props: {
    initialPets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pets: [...this.initialPets],
      newPet: {
        name: '',
        species: ''
      }
    };
  },
  computed: {
    canAddPet() {
      return this.newPet.name.trim() && this.newPet.species.trim();
    }
  },
  methods: {
    addPet() {
      if (this.canAddPet) {
        this.pets.push({
          name: this.newPet.name.trim(),
          species: this.newPet.species.trim(),
          imageIndices: [] // Array of image indices for this pet
        });
        this.newPet = { name: '', species: '' };
        this.$emit('petsUpdated', this.pets);
      }
    },
    addPetIfValid() {
      if (this.canAddPet) {
        this.addPet();
      }
    },
    removePet(index) {
      this.pets.splice(index, 1);
      this.$emit('petsUpdated', this.pets);
    },
    goBack() {
      this.$emit('back');
    },
    proceed() {
      if (this.pets.length > 0) {
        this.$emit('proceed', this.pets);
      }
    }
  }
};
</script>

<style scoped>
.multiple-pets-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2vw;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.pets-header {
  text-align: center;
  margin-bottom: 3vw;
}

.pets-title {
  font-size: 2.5vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5vw;
}

.pets-subtitle {
  font-size: 1.2vw;
  color: #666;
}

.pets-list {
  flex: 1;
  margin-bottom: 3vw;
}

.pet-item {
  display: flex;
  align-items: center;
  gap: 1vw;
  padding: 1vw;
  background: #f8f9fa;
  border-radius: 2vw;
  margin-bottom: 1vw;
}

.pet-name-tag,
.pet-species-tag {
  padding: 0.5vw 1.5vw;
  background: white;
  border: 1px solid #ddd;
  border-radius: 2vw;
  font-size: 1vw;
  color: #333;
}

.remove-pet {
  margin-left: auto;
  width: 2vw;
  height: 2vw;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.remove-pet:hover {
  transform: scale(1.1);
}

.add-pet-form {
  margin: 2vw 0;
}

.input-group {
  display: flex;
  gap: 1vw;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.input-icon {
  position: absolute;
  left: 1vw;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2vw;
}

.pet-input {
  width: 100%;
  padding: 1vw 1vw 1vw 3vw;
  font-size: 1vw;
  border: 1px solid #ddd;
  border-radius: 2vw;
  transition: border-color 0.3s;
}

.pet-input:focus {
  outline: none;
  border-color: #666;
}

.add-pet-button {
  width: 100%;
  padding: 1vw;
  border: 2px dashed #ddd;
  border-radius: 1vw;
  background: white;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-pet-button:hover:not(:disabled) {
  border-color: #999;
  background: #f8f9fa;
}

.add-pet-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-pet-button svg {
  width: 2vw;
  height: 2vw;
  color: #666;
}

.navigation-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2vw;
}

.nav-arrow {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-arrow:hover:not(:disabled) {
  background: #f5f5f5;
  transform: scale(1.1);
}

.nav-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.pets-count {
  font-size: 1.1vw;
  color: #666;
}

/* Mobile styles */
@media (max-width: 768px) {
  .multiple-pets-container {
    padding: 4vw;
  }

  .pets-title {
    font-size: 6vw;
    margin-bottom: 2vw;
  }

  .pets-subtitle {
    font-size: 3.5vw;
  }

  .pet-item {
    padding: 3vw;
    gap: 2vw;
    margin-bottom: 3vw;
  }

  .pet-name-tag,
  .pet-species-tag {
    padding: 2vw 4vw;
    font-size: 3.5vw;
    border-radius: 5vw;
  }

  .remove-pet {
    width: 6vw;
    height: 6vw;
  }

  .remove-pet svg {
    width: 4vw;
    height: 4vw;
  }

  .add-pet-form {
    margin: 5vw 0;
  }

  .input-group {
    gap: 3vw;
  }

  .input-icon {
    left: 3vw;
    font-size: 4vw;
  }

  .pet-input {
    padding: 3vw 3vw 3vw 8vw;
    font-size: 3.5vw;
    border-radius: 5vw;
  }

  .add-pet-button {
    padding: 3vw;
    border-radius: 3vw;
  }

  .add-pet-button svg {
    width: 6vw;
    height: 6vw;
  }

  .navigation-container {
    padding: 0 4vw;
  }

  .nav-arrow {
    width: 8vw;
    height: 8vw;
    border: 0.3vw solid #333;
  }

  .nav-arrow svg {
    width: 4vw;
    height: 4vw;
  }

  .pets-count {
    font-size: 3vw;
  }
}
</style> 